<template>
  <el-dialog
    :visible="true"
    :fullscreen="true"
    custom-class="sticky-footer-dialog"
    :close-on-click-modal="false"
    :before-close="close"
  >
    <div slot="title">
      {{ dialogTitle }}
      <el-tag
        v-if="desadv"
        size="small"
        :type="statusType(desadv.status)"
        style="margin-left: 5px;"
      >
        {{ statusLabel(desadv.status) }}
      </el-tag>
    </div>

    <div v-loading="loading">
      <template v-if="desadv">
        <!-- ============ DOCUMENT HEADER (read-only) ============ -->
        <div class="doc-meta">
          <div class="meta-item">
            <div class="meta-label">
              Vevői megrendelés
            </div>
            <div class="meta-value">
              <el-link
                v-if="canViewCustomerOrder"
                type="primary"
                :underline="false"
                @click="openCustomerOrder"
              >
                {{ orderReceiptNumber || '—' }}
              </el-link>
              <template v-else>
                {{ orderReceiptNumber || '—' }}
              </template>
            </div>
          </div>
          <div class="meta-item">
            <div class="meta-label">
              Vevő
            </div>
            <div class="meta-value">
              {{ desadv.customer_name || '—' }}
            </div>
          </div>
          <div class="meta-item">
            <div class="meta-label">
              Megrendelés szerinti szállítás
            </div>
            <div class="meta-value">
              {{ formatDate(desadv.customer_order_delivery_date) }}
            </div>
          </div>
          <div class="meta-actions">
            <loading-list-link :id="desadv.id" />
          </div>
        </div>

        <!-- pallets -->
        <div class="section-head">
          <h4>Raklapok ({{ desadv.pallets.length }})</h4>
          <el-button
            v-if="desadv.status === 'open'"
            v-can="'desadv.update'"
            type="success"
            size="small"
            icon="el-icon-plus"
            :loading="addingPallet"
            @click="addPallet"
          >
            Új raklap
          </el-button>
        </div>
        <el-table
          :data="desadv.pallets"
          size="small"
          style="width:100%; margin-bottom: 24px;"
        >
          <el-table-column
            label="#"
            prop="position"
            width="60"
          />
          <el-table-column
            label="Raklap típusa"
            min-width="180"
          >
            <template slot-scope="scope">
              {{ scope.row.pallet_product ? scope.row.pallet_product.name : '—' }}
            </template>
          </el-table-column>
          <el-table-column
            label="Bruttó tömeg"
            width="130"
          >
            <template slot-scope="scope">
              {{ scope.row.gross_weight }} kg
            </template>
          </el-table-column>
          <el-table-column
            label="Tételek"
            width="90"
            align="center"
          >
            <template slot-scope="scope">
              {{ scope.row.items.length }}
            </template>
          </el-table-column>
          <el-table-column
            label="Státusz"
            min-width="190"
          >
            <template slot-scope="scope">
              <el-tag
                v-if="scope.row.finalized_at"
                size="small"
                type="success"
              >
                Véglegesítve
              </el-tag>
              <el-tag
                v-else
                size="small"
                type="info"
              >
                Piszkozat
              </el-tag>
              <div
                v-if="scope.row.finalized_at && scope.row.sscc"
                style="font-size:12px; color:#909399; margin-top:2px;"
              >
                SSCC: {{ scope.row.sscc }}
              </div>
            </template>
          </el-table-column>
          <el-table-column
            label="Műveletek"
            width="340"
          >
            <template slot-scope="scope">
              <div class="pallet-actions">
                <el-link
                  v-can="'desadv.update'"
                  type="primary"
                  :underline="false"
                  :icon="scope.row.finalized_at ? 'el-icon-view' : 'el-icon-edit'"
                  @click.stop="editPallet(scope.row)"
                >
                  {{ scope.row.finalized_at ? 'Megtekintés' : 'Szerkesztés' }}
                </el-link>
                <el-link
                  v-if="desadv.status === 'open' && !scope.row.finalized_at"
                  v-can="'desadv.update'"
                  type="warning"
                  :underline="false"
                  icon="el-icon-circle-check"
                  @click.stop="finalizePallet(scope.row)"
                >
                  Véglegesítés
                </el-link>
                <pallet-label-link
                  v-if="scope.row.finalized_at"
                  :id="scope.row.id"
                  label="Címke"
                />
                <el-link
                  v-if="desadv.status === 'open'"
                  v-can="'desadv.update'"
                  type="danger"
                  :underline="false"
                  icon="el-icon-delete"
                  @click.stop="deletePallet(scope.row)"
                >
                  Törlés
                </el-link>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- coverage -->
        <h4>Megrendelés lefedettsége</h4>
        <coverage-table
          :source-items="desadv.items"
          :coverage="desadv.coverage"
          :completeness="completenessItems"
          :highlight-incomplete="desadv.status !== 'open'"
        />

        <!-- shipments (trucks) -->
        <desadv-shipments
          style="margin-top: 24px;"
          :desadv="desadv"
          @refresh="loadDetail"
        />
      </template>
    </div>

    <!-- nested layer: the pallet editor opens on top of this dialog -->
    <desadv-pallet-editor
      v-if="palletLayerId"
      :key="palletLayerId"
      :pallet-id="palletLayerId"
      @close="closePalletLayer"
    />

    <div
      v-if="desadv"
      slot="footer"
      class="dialog-footer"
    >
      <el-button @click="close">
        Bezárás
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import CoverageTable from './CoverageTable';
import DesadvShipments from './Shipments';
import DesadvPalletEditor from './PalletEditor';
import PalletLabelLink from '../../utils/desadv/PalletLabelLink';
import LoadingListLink from '../../utils/desadv/LoadingListLink';

export default {
  components: {
    CoverageTable,
    DesadvShipments,
    DesadvPalletEditor,
    PalletLabelLink,
    LoadingListLink,
  },
  props: {
    desadvId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      addingPallet: false,
      desadv: null,
    };
  },
  computed: {
    dialogTitle() {
      return 'DESADV szállítólevél' + (this.despatchAdviceNumbers ? ' – ' + this.despatchAdviceNumbers : '');
    },
    despatchAdviceNumbers() {
      const shipments = (this.desadv && this.desadv.shipments) || [];
      return shipments.map(s => s.despatch_advice_number).filter(Boolean).join(', ');
    },
    orderReceiptNumber() {
      return (this.desadv && this.desadv.customer_order_receipt_number) || '';
    },
    canViewCustomerOrder() {
      return !!(this.desadv && this.desadv.customer_order_id && this.can('customerorder.view'));
    },
    // GET desadvs/{id} returns completeness as { complete, items }.
    completenessItems() {
      return (this.desadv && this.desadv.completeness && this.desadv.completeness.items) || [];
    },
    palletLayerId() {
      return this.$route.params.palletId || null;
    },
  },
  watch: {
    // The layer is torn down by any route change (footer button, ESC, browser Back),
    // so the refresh is driven off the route instead of the editor's close event.
    palletLayerId(newId, oldId) {
      if (oldId && !newId) this.loadDetail();
    },
  },
  async mounted() {
    await this.loadDetail();
  },
  methods: {
    can(permission) {
      const user = this.$store.getters['auth/user'];
      return user && user.permissions && user.permissions.includes(permission);
    },
    close() {
      this.$router.replace('/sales/desadvs');
    },
    formatDate(value) {
      return value ? this.$options.filters.date(value) : '—';
    },
    openCustomerOrder() {
      this.$router.push('/sales/customer-orders/view/' + this.desadv.customer_order_id);
    },
    statusLabel(status) {
      const labels = {
        open: 'Nyitott',
        finalized: 'Véglegesítve',
        partially_shipped: 'Részben szállítva',
        shipped: 'Szállítva',
      };
      return labels[status] || status;
    },
    statusType(status) {
      const types = {
        open: 'info',
        finalized: '',
        partially_shipped: 'warning',
        shipped: 'success',
      };
      return types[status] != null ? types[status] : 'info';
    },
    async loadDetail() {
      this.loading = true;
      try {
        this.desadv = await this.$store.dispatch('desadv/fetch', this.desadvId);
      } catch (e) {
      }
      this.loading = false;
    },
    async addPallet() {
      this.addingPallet = true;
      try {
        const response = await this.$store.dispatch('desadv/createPallet', this.desadv.id);
        this.openPalletLayer(response.id);
      } catch (e) {
      }
      this.addingPallet = false;
    },
    editPallet(pallet) {
      this.openPalletLayer(pallet.id);
    },
    // The pallet layer is route driven so it stays deep linkable; this dialog is not remounted,
    // because the route keeps the same DESADV id.
    openPalletLayer(palletId) {
      this.$router.push('/sales/desadvs/' + this.desadv.id + '/pallets/' + palletId);
    },
    closePalletLayer() {
      this.$router.replace('/sales/desadvs/view/' + this.desadvId);
    },
    finalizePallet(pallet) {
      this.$confirm(
        'A véglegesítés kiosztja a raklap SSCC kódját, és a raklap többé nem szerkeszthető. Folytatja?',
        'Raklap véglegesítése',
        { confirmButtonText: 'Véglegesítés', cancelButtonText: 'Mégse', type: 'warning' },
      ).then(async () => {
        await this.$store.dispatch('desadv/finalizePallet', pallet.id);
        await this.loadDetail();
      }).catch(() => {});
    },
    deletePallet(pallet) {
      this.$confirm(`Biztosan törli a(z) ${pallet.position}. raklapot?`, 'Megerősítés', {
        confirmButtonText: 'Törlés',
        cancelButtonText: 'Mégse',
        type: 'warning',
      }).then(async () => {
        await this.$store.dispatch('desadv/deletePallet', pallet.id);
        await this.loadDetail();
      }).catch(() => {});
    },
  },
};
</script>
<style scoped>
  .doc-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding-bottom: 16px;
    border-bottom: 1px solid #EBEEF5;
    margin-bottom: 20px;
  }
  .meta-item {
    margin-right: 40px;
  }
  .meta-actions {
    margin-left: auto;
  }
  .meta-label {
    font-size: 12px;
    color: #909399;
    margin-bottom: 2px;
  }
  .meta-value {
    font-size: 14px;
    color: #606266;
  }
  .section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }
  .pallet-actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }
</style>
